/**
 * Coordenação de flush: rede, circuit breaker, leader tab, distributed lock.
 */

import { getNetworkOnline } from '../network';
import { isCircuitOpen } from '../circuitBreaker';
import { isLeaderTab, getTabId, getMinLeaderFencingToken } from '../tabCoordinator';
import {
  tryAcquireFlushLock,
  releaseFlushLock,
  renewFlushLock,
  getFlushLockFencingToken,
  getFlushLockHolder,
  validateFencingToken,
} from '../coordination/distributedLock';
import { getCoordinationEpoch } from '../coordination/coordinationEpoch';
import { FLUSH_LOCK_HEARTBEAT_MS } from '../coordination/coordinationConstants';
import { updateCoordinationTelemetry } from '../telemetry';
import { isStorageSafeMode } from '../storage/storageSafeMode';

export type FlushGateReason =
  | 'offline'
  | 'circuit_open'
  | 'not_leader'
  | 'lock_unavailable'
  | 'storage_unsafe';

export interface FlushGateResult {
  allowed: boolean;
  reason?: FlushGateReason;
}

let flushFencingTokenAtAcquire = 0;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

export function evaluateFlushGate(): FlushGateResult {
  if (isStorageSafeMode()) return { allowed: false, reason: 'storage_unsafe' };
  if (!getNetworkOnline()) return { allowed: false, reason: 'offline' };
  if (isCircuitOpen()) return { allowed: false, reason: 'circuit_open' };
  if (!isLeaderTab()) return { allowed: false, reason: 'not_leader' };
  if (!tryAcquireFlushLock()) return { allowed: false, reason: 'lock_unavailable' };
  const lockToken = getFlushLockFencingToken();
  const leaderMin = getMinLeaderFencingToken();
  flushFencingTokenAtAcquire = Math.max(lockToken, leaderMin);
  updateCoordinationTelemetry({
    isLeader: true,
    lockHolder: getFlushLockHolder(),
    epoch: getCoordinationEpoch(),
    fencingToken: flushFencingTokenAtAcquire,
  });
  return { allowed: true };
}

export function renewFlushCoordination(): boolean {
  const ok = renewFlushLock();
  if (ok) {
    updateCoordinationTelemetry({
      lockHolder: getTabId(),
      fencingToken: getFlushLockFencingToken(),
      epoch: getCoordinationEpoch(),
    });
  }
  return ok;
}

export function releaseFlushCoordination(): void {
  stopFlushHeartbeat();
  releaseFlushLock();
  updateCoordinationTelemetry({
    lockHolder: null,
    fencingToken: 0,
  });
}

export function assertFlushCoordinationValid(): boolean {
  return validateFencingToken(flushFencingTokenAtAcquire);
}

export function startFlushHeartbeat(intervalMs = FLUSH_LOCK_HEARTBEAT_MS): void {
  stopFlushHeartbeat();
  heartbeatTimer = setInterval(() => {
    renewFlushCoordination();
  }, intervalMs);
}

export function stopFlushHeartbeat(): void {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

